import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import blogData from '../data/blog.json';
import ReactMarkdown from 'react-markdown';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    // Find the post with matching slug
    const currentPost = blogData.find(post => post.slug === slug);
    setPost(currentPost);

    // Find related posts (posts with same tags)
    if (currentPost) {
      const related = blogData
        .filter(p => p.id !== currentPost.id && p.tags.some(tag => currentPost.tags.includes(tag)))
        .slice(0, 2); // Get up to 2 related posts
      setRelatedPosts(related);
    }
  }, [slug]);

  if (!post) {
    return (
      <Container>
        <h2>Post not found</h2>
        <BackLink to="/blog">← Back to Blog</BackLink>
      </Container>
    );
  }

  return (
    <Container>
      <BackLink to="/blog">← Back to Blog</BackLink>
      
      <PostWrapper
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <PostHeader>
          <PostTitle>{post.title}</PostTitle>
          <PostMeta>
            <PostDate>
              {new Date(post.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </PostDate>
            <TagsContainer>
              {post.tags.map((tag, index) => (
                <Tag key={index}>#{tag}</Tag>
              ))}
            </TagsContainer>
          </PostMeta>
        </PostHeader>
        
        <FeaturedImage src={post.image} alt={post.title} />
        
        <PostContent>
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </PostContent>
      </PostWrapper>

      {relatedPosts.length > 0 && (
        <RelatedPostsSection>
          <SectionTitle>Related Posts</SectionTitle>
          <RelatedPostsGrid>
            {relatedPosts.map(relatedPost => (
              <RelatedPostCard key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                <RelatedPostImage src={relatedPost.image} alt={relatedPost.title} />
                <RelatedPostContent>
                  <RelatedPostTitle>{relatedPost.title}</RelatedPostTitle>
                  <RelatedPostDate>
                    {new Date(relatedPost.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </RelatedPostDate>
                </RelatedPostContent>
              </RelatedPostCard>
            ))}
          </RelatedPostsGrid>
        </RelatedPostsSection>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 120px 20px 50px;
  max-width: 800px;
  margin: 0 auto;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 2rem;
  color: var(--text-secondary);
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.9rem;
  text-decoration: none;
  transition: var(--transition);
  
  &:hover {
    color: var(--accent);
  }
`;

const PostWrapper = styled(motion.article)`
  background: var(--secondary);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
`;

const PostHeader = styled.div`
  padding: 2rem 2rem 1.5rem;
`;

const PostTitle = styled.h1`
  font-size: 2.5rem;
  color: var(--text-primary);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PostMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
`;

const PostDate = styled.span`
  color: var(--text-secondary);
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.9rem;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  font-size: 0.8rem;
  color: var(--accent);
`;

const FeaturedImage = styled.img`
  width: 100%;
  max-height: 400px;
  object-fit: cover;
`;

const PostContent = styled.div`
  padding: 2rem;
  color: var(--text-primary);
  line-height: 1.8;
  
  h1, h2, h3, h4, h5, h6 {
    color: var(--text-primary);
    margin: 2rem 0 1rem;
  }
  
  h1 {
    font-size: 2rem;
  }
  
  h2 {
    font-size: 1.75rem;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
  
  a {
    color: var(--accent);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
  
  blockquote {
    border-left: 3px solid var(--accent);
    padding-left: 1rem;
    font-style: italic;
    margin: 1.5rem 0;
  }
  
  pre {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
    padding: 1rem;
    overflow-x: auto;
    margin: 1.5rem 0;
  }
  
  code {
    font-family: 'SF Mono', 'Fira Code', monospace;
    font-size: 0.9rem;
  }
  
  hr {
    border: 0;
    height: 1px;
    background-color: var(--text-secondary);
    margin: 2rem 0;
  }
`;

const RelatedPostsSection = styled.div`
  margin-top: 3rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
`;

const RelatedPostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const RelatedPostCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background: var(--secondary);
  border-radius: 8px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0 5px 15px -10px rgba(2, 12, 27, 0.7);
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const RelatedPostImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
`;

const RelatedPostContent = styled.div`
  padding: 1rem;
`;

const RelatedPostTitle = styled.h4`
  color: var(--text-primary);
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
`;

const RelatedPostDate = styled.span`
  color: var(--text-secondary);
  font-size: 0.8rem;
`;

export default BlogPost;